// Pure helpers for the Edit user panel in Access Management. Kept free of React/MUI so the
// rules that decide what an edit may change, and what it sends, can be unit tested in isolation.
//
// An edit is up to three Identity changes (name, roles, sites) followed by the Admin-Api
// provisioning upsert the Add User flow uses. Two behaviours shape the rules here:
//   - re-sending an Identity scope row REPLACES its school list and its data restrictions, so a
//     site change carries the user's existing restrictions forward rather than clearing them;
//   - provisioning sets a user's dashboards up for ONE school or trust, rewriting them to exactly
//     what it is sent. Roles and sites are therefore only changeable for someone whose access sits
//     in a single organisation, and provisioning is always built from that whole organisation —
//     never from the page the panel was opened on.

import { SCOPE_TYPE } from './userFields';
import { SAFE_SPACE_ROLE_CODES, roleNumericId } from './roleCatalog';

const RESTRICTION_FLAGS = ['restrictToTaughtChildren', 'restrictStaffData', 'restrictPupilData'];

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const uniqueIds = (values) => [...new Set((values || []).map(toNumber).filter((v) => v !== null))];

const pick = (source, camel, pascal) => (source?.[camel] !== undefined ? source[camel] : source?.[pascal]);

const coversAll = (selected, all) =>
  all.length > 0 && selected.length === all.length && all.every((id) => selected.includes(id));

function readScope(raw) {
  const scope = {
    type: String(pick(raw, 'type', 'Type') || ''),
    entityIds: uniqueIds(pick(raw, 'entityIds', 'EntityIds')),
    isActive: pick(raw, 'isActive', 'IsActive') !== false,
    schoolRestrictions: (pick(raw, 'schoolRestrictions', 'SchoolRestrictions') || []).map((school) => ({
      schoolId: toNumber(pick(school, 'schoolId', 'SchoolId')),
      restrictToTaughtChildren: pick(school, 'restrictToTaughtChildren', 'RestrictToTaughtChildren') === true,
      restrictStaffData: pick(school, 'restrictStaffData', 'RestrictStaffData') === true,
      restrictPupilData: pick(school, 'restrictPupilData', 'RestrictPupilData') === true,
    })),
  };
  RESTRICTION_FLAGS.forEach((flag) => {
    scope[flag] = pick(raw, flag, flag.charAt(0).toUpperCase() + flag.slice(1)) === true;
  });
  return scope;
}

// Reads GET /users/{id} into what the panel needs. Only active scope rows count as access.
export function readProfile(profile) {
  const roles = pick(profile, 'roles', 'Roles') || [];
  const scopes = (pick(profile, 'scopes', 'Scopes') || []).map(readScope).filter((s) => s.isActive);
  const staffLinks = (pick(profile, 'staffLinks', 'StaffLinks') || [])
    .map((link) => ({
      schoolId: toNumber(pick(link, 'schoolId', 'SchoolId')),
      staffId: toNumber(pick(link, 'staffId', 'StaffId')),
    }))
    .filter((link) => link.schoolId !== null && link.staffId);

  return {
    firstName: pick(profile, 'firstName', 'FirstName') || '',
    lastName: pick(profile, 'lastName', 'LastName') || '',
    email: pick(profile, 'email', 'Email') || '',
    status: pick(profile, 'status', 'Status') || '',
    lastLoginAt: pick(profile, 'lastLoginAt', 'LastLoginAt') || null,
    roleCodes: [...new Set(roles.map((role) => pick(role, 'code', 'Code')).filter(Boolean))],
    roleNamesByCode: Object.fromEntries(
      roles
        .map((role) => [
          pick(role, 'code', 'Code'),
          pick(role, 'displayName', 'DisplayName') || pick(role, 'name', 'Name'),
        ])
        .filter(([code]) => Boolean(code))
    ),
    scopes,
    staffLinks,
  };
}

const idsOfType = (scopes, type) =>
  uniqueIds(scopes.filter((s) => s.type === type).flatMap((s) => s.entityIds));

// The trusts the user is granted on — whose details (name, schools) the panel needs to load.
export const grantedGroupIds = (scopes) => idsOfType(scopes, 'Group');

// Groups the user's access into the organisations it reaches: one card per trust they're granted
// on (with the schools of that trust they reach), and one per school they reach outside those
// trusts. `groupSchoolIdsById` holds each trust's own school list; a trust missing from it means
// its details couldn't be loaded, so the picture is incomplete.
export function buildOrganisations(scopes, groupSchoolIdsById = {}) {
  const schoolIds = idsOfType(scopes, 'School');
  const organisations = [];
  const claimed = new Set();
  let complete = true;

  grantedGroupIds(scopes).forEach((groupId) => {
    const known = Array.isArray(groupSchoolIdsById[groupId]);
    if (!known) complete = false;
    const groupSchoolIds = known ? uniqueIds(groupSchoolIdsById[groupId]) : [];
    const reached = schoolIds.filter((id) => groupSchoolIds.includes(id));
    reached.forEach((id) => claimed.add(id));
    const allSchools = reached.length === 0 || coversAll(reached, groupSchoolIds);
    organisations.push({
      kind: 'group',
      id: groupId,
      groupSchoolIds,
      siteIds: allSchools ? [] : reached,
      allSchools,
    });
  });

  schoolIds
    .filter((id) => !claimed.has(id))
    .forEach((id) => organisations.push({ kind: 'school', id }));

  return { organisations, complete };
}

// The organisation that matches the page the panel was opened on: the trust itself (or one of its
// schools) on a trust page; the school itself (or its trust) on a school page.
export function findCurrentOrganisation(organisations, { scope, schoolId, groupId, groupSchoolIds = [] }) {
  if (scope === 'group') {
    const thisGroup = toNumber(groupId);
    const sites = uniqueIds(groupSchoolIds);
    return (
      organisations.find((o) => o.kind === 'group' && o.id === thisGroup) ||
      organisations.find((o) => o.kind === 'school' && sites.includes(o.id)) ||
      null
    );
  }
  const thisSchool = toNumber(schoolId);
  return (
    organisations.find((o) => o.kind === 'school' && o.id === thisSchool) ||
    organisations.find((o) => o.kind === 'group' && o.groupSchoolIds.includes(thisSchool)) ||
    null
  );
}

// What this edit may change, and why not when it can't. Names are always editable; roles and the
// dashboard sync need the user's access to sit in exactly one organisation; sites are edited on
// the trust's own page, for someone granted on that trust.
export function planEdit({ organisations, complete, current, scope, firstName }) {
  const who = firstName || 'This user';
  let rolesLockedReason = null;
  if (!complete) {
    rolesLockedReason = 'We couldn’t load all of this user’s organisations, so roles can’t be changed right now. Try again shortly.';
  } else if (organisations.length === 0) {
    rolesLockedReason = `${who} doesn’t have access to a school or trust yet, so there are no roles to change here.`;
  } else if (organisations.length > 1) {
    rolesLockedReason = `${who} has access in ${organisations.length} organisations. Roles can only be changed for someone in a single school or trust, because their dashboards are set up for one.`;
  }

  const rolesEditable = rolesLockedReason === null;
  const only = rolesEditable ? organisations[0] : null;
  const sitesEditable = rolesEditable && scope === 'group' && only.kind === 'group' && current === only;

  const provisioningTarget = !only
    ? null
    : only.kind === 'group'
      ? { scope: 'group', groupId: only.id, allSiteIds: only.groupSchoolIds, initialSiteIds: only.siteIds }
      : { scope: 'school', schoolId: only.id, allSiteIds: [], initialSiteIds: [] };

  return { rolesEditable, rolesLockedReason, sitesEditable, provisioningTarget };
}

// True when two site selections grant the same schools. [] means "all schools", so it equals
// an explicit pick of every school in the trust.
export function sameSites(a, b, allSiteIds = []) {
  const all = uniqueIds(allSiteIds);
  const normalise = (ids) => {
    const unique = uniqueIds(ids);
    return unique.length === 0 || coversAll(unique, all) ? [] : [...unique].sort((x, y) => x - y);
  };
  const left = normalise(a);
  const right = normalise(b);
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

// A short, human description of a site selection: "All schools", or the names (then "+N more").
export function describeSites(siteIds = [], namesById = {}, { max = 3 } = {}) {
  const ids = uniqueIds(siteIds);
  if (ids.length === 0) return 'All schools';
  const names = ids.map((id) => namesById[id] || `School ${id}`);
  return names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} +${names.length - max} more`;
}

// Roles to add and remove. Only roles the user can change here are removed: a held role that
// isn't changeable (e.g. a SafeSpace role while several schools are selected) is left as it is.
export function diffRoles({ heldCodes = [], changeableCodes = [], selectedCodes = [] }) {
  const changeable = new Set(changeableCodes);
  const selected = new Set(selectedCodes);
  const held = new Set(heldCodes);
  return {
    toAssign: selectedCodes.filter((code) => changeable.has(code) && !held.has(code)),
    toRemove: heldCodes.filter((code) => changeable.has(code) && !selected.has(code)),
  };
}

// The roles the user will hold after the edit: the changeable ones picked, plus every held role
// that can't be changed here.
export function rolesAfterEdit({ heldCodes = [], changeableCodes = [], selectedCodes = [] }) {
  const changeable = new Set(changeableCodes);
  return [
    ...new Set([
      ...heldCodes.filter((code) => !changeable.has(code)),
      ...selectedCodes.filter((code) => changeable.has(code)),
    ]),
  ];
}

// The numeric role ids the provisioning `access` array expects (roles without one are skipped).
export function provisioningRoleIds(codes = []) {
  return [...new Set(codes)].map(roleNumericId).filter((id) => id !== undefined && id !== null);
}

// The roles that need a school-staff link: SafeSpace roles always, Owner in a single school.
export function staffLinkedRoleCodes(codes = [], singleSchool = false) {
  return [
    ...codes.filter((code) => SAFE_SPACE_ROLE_CODES.includes(code)),
    ...(singleSchool && codes.includes('Owner') ? ['Owner'] : []),
  ];
}

// Carries the user's current data restrictions onto the scope assignments an edit re-sends, so
// a site change never loosens what the user may see. A per-school override survives only for
// schools the new selection still reaches.
export function withKeptRestrictions(assignments, scopes, { groupId, groupSchoolIds = [] } = {}) {
  const thisGroup = toNumber(groupId);
  const groupSchools = uniqueIds(groupSchoolIds);
  const reachedSchools = uniqueIds(
    assignments.filter((a) => a.scopeType === SCOPE_TYPE.school).flatMap((a) => a.entityIds)
  );

  const currentRowFor = (assignment) =>
    assignment.scopeType === SCOPE_TYPE.group
      ? scopes.find((s) => s.type === 'Group' && s.entityIds.includes(thisGroup))
      : scopes.find((s) => s.type === 'School' && s.entityIds.some((id) => groupSchools.includes(id)));

  return assignments.map((assignment) => {
    const current = currentRowFor(assignment);
    if (!current) return assignment;

    const reach = assignment.scopeType === SCOPE_TYPE.school ? uniqueIds(assignment.entityIds) : reachedSchools;
    const kept = { ...assignment };
    RESTRICTION_FLAGS.forEach((flag) => {
      kept[flag] = current[flag];
    });
    kept.schoolRestrictions = current.schoolRestrictions.filter(
      (school) => reach.length === 0 || reach.includes(school.schoolId)
    );
    return kept;
  });
}
